// The bag's customer box: find a customer by mobile (or name) through Core's
// customer search, then pick them onto the bill with Core's selectCustomer.
// Enter always looks the number up first; registering a new customer is only
// offered once a lookup has come back empty, so a known customer is never
// registered twice.
import React, { useEffect, useRef, useState } from "react";
import { initials, lookupKey } from "./floorHelpers";

export default function FloorCustomer({
  customer,
  named,
  isOnline,
  handleCustomerSearch,
  customerSearchResults,
  setCustomerSearchResults,
  setCustomerSearchInput,
  selectCustomer,
  showAddNewCustomerFields,
  consent,
  consentBusy,
  onConsent,
}) {
  const [text, setText] = useState("");
  const [status, setStatus] = useState("idle"); // idle | searching | done
  const [editing, setEditing] = useState(false);
  const [searched, setSearched] = useState("");
  const autoPick = useRef(false);
  const offline = isOnline === false;
  const canSearch = typeof handleCustomerSearch === "function" && typeof selectCustomer === "function";
  const results = status === "done" ? customerSearchResults || [] : [];

  const pick = async (index) => {
    await selectCustomer(index);
    setEditing(false);
    setText("");
    setStatus("idle");
  };

  const lookup = async (auto) => {
    const key = lookupKey(text);
    if (!key || status === "searching" || !canSearch || offline) return;
    // Clear the last search so its rows can't be picked for this one.
    setCustomerSearchResults?.([]);
    setCustomerSearchInput?.(key);
    autoPick.current = auto;
    setSearched(key);
    setStatus("searching");
    try {
      await handleCustomerSearch(key);
    } finally {
      setStatus("done");
    }
  };

  // Enter on a number that matches exactly one customer picks them.
  useEffect(() => {
    if (status !== "done" || !autoPick.current) return;
    autoPick.current = false;
    if ((customerSearchResults || []).length === 1) pick(0);
  }, [status, customerSearchResults]); // eslint-disable-line react-hooks/exhaustive-deps

  if (named && !editing) {
    return (
      <section className="fp-fl-cbox" aria-label="Customer">
        <div className="fp-fl-row fp-fl-nowrap">
          <span className="fp-fl-av" aria-hidden="true">
            {initials(customer.name)}
          </span>
          <div className="fp-fl-grow">
            <b>{customer.name}</b>
            <small className="fp-fl-mute fp-num">{[customer.mobileNo, customer.loyaltyLevel?.name].filter(Boolean).join(" · ")}</small>
          </div>
          {canSearch && (
            <button type="button" className="fp-fl-link" onClick={() => setEditing(true)}>
              Change
            </button>
          )}
        </div>
        <label className="fp-fl-consent">
          <input type="checkbox" checked={Boolean(consent)} disabled={Boolean(consentBusy)} aria-busy={consentBusy ? "true" : "false"} onChange={(e) => onConsent(e.target.checked)} />
          <span>{customer.name} agrees to get offers on WhatsApp. Receipts are sent either way.</span>
        </label>
      </section>
    );
  }

  return (
    <section className="fp-fl-cbox" aria-label="Customer">
      <label className="fp-fl-lbl" htmlFor="fp-fl-cq">
        Customer
      </label>
      {!canSearch ? (
        <span className="fp-fl-hint">Add the customer at the till.</span>
      ) : (
        <>
          <form
            className="fp-fl-srch"
            onSubmit={(e) => {
              e.preventDefault();
              lookup(true);
            }}
          >
            <input
              id="fp-fl-cq"
              className="fp-fl-field fp-num"
              inputMode="tel"
              autoComplete="off"
              enterKeyHint="search"
              placeholder="Mobile number, then Enter"
              value={text}
              disabled={offline}
              aria-label="Customer mobile or name"
              onChange={(e) => {
                setText(e.target.value);
                setStatus("idle");
              }}
            />
          </form>
          {status === "searching" && (
            <span className="fp-fl-hint" role="status">
              Finding {searched}…
            </span>
          )}
          {offline && <span className="fp-fl-hint">Finding a customer needs a connection.</span>}
          {results.map((c, i) => (
            <button key={c.cwrCustomerId || i} type="button" className="fp-fl-chit" onClick={() => pick(i)}>
              <b>{c.name}</b>
              <span className="fp-fl-mute fp-num">{c.mobileNo}</span>
            </button>
          ))}
          {status === "done" && results.length === 0 && (
            <div className="fp-fl-none" role="status">
              <span>No customer found for {searched}.</span>
              {typeof showAddNewCustomerFields === "function" && (
                <button type="button" className="fp-fl-btn" onClick={() => showAddNewCustomerFields()}>
                  Add new customer
                </button>
              )}
            </div>
          )}
          {editing && (
            <button type="button" className="fp-fl-link" onClick={() => setEditing(false)}>
              Keep {customer?.name}
            </button>
          )}
        </>
      )}
    </section>
  );
}
